/* =============================================================================
 * Turning whatever came off a phone into a profile picture
 * -----------------------------------------------------------------------------
 * The file a camera hands over is a four-megabyte twelve-megapixel JPEG in
 * whatever shape the phone was held. Sending that up as somebody's avatar costs
 * a site worker four megabytes of a metered connection to produce a picture
 * that is displayed at thirty-two pixels across.
 *
 * So it is reduced here, before anything is sent:
 *
 *   - centre-cropped square, because every place it appears is a circle and a
 *     portrait squeezed into one is a portrait of somebody's forehead
 *   - scaled to 512, which is four times the largest size any screen shows and
 *     leaves room for a bigger one later without re-uploading everybody
 *   - encoded WebP at 0.82, which lands around 30–60 kB
 *
 * One canonical size rather than a set. Avatars are small and square by nature,
 * so every display is a downscale of the same file — and asking ImageKit for
 * per-size variants would mean the transformation parameters joining the signed
 * path, which is a change to the signing code and a much larger thing to get
 * wrong.
 *
 * WHY NOT JUST LET IMAGEKIT DO IT
 * -----------------------------------------------------------------------------
 * It would, on the way out. It cannot help on the way in, and the way in is
 * where the four megabytes are actually spent — on a phone, on site, on a
 * connection that is being paid for by the megabyte.
 * ========================================================================== */

/** The stored edge, in pixels. Four times the largest size anything renders. */
export const AVATAR_SIZE = 512;

/** Quality for the WebP encode. Above 0.85 the file doubles for no visible gain. */
const QUALITY = 0.82;

/** What a file picker should offer, and what the upload function accepts. */
export const AVATAR_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';

/**
 * The largest original worth trying to read.
 *
 * Not the limit on what is uploaded — that is the reduced file, which is tiny.
 * This is a guard on decoding: a 50 MB image decodes to a bitmap several
 * hundred megabytes wide in memory, and a mid-range Android phone does not fail
 * gracefully, it stops.
 */
export const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

export class AvatarImageError extends Error {}

/**
 * Reduces one chosen image to the square WebP that gets stored.
 *
 * Throws AvatarImageError with a sentence worth showing when the file is not an
 * image, is too large to decode, or the browser refuses to encode it.
 */
export async function prepareAvatar(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) {
    throw new AvatarImageError('That is not an image.');
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new AvatarImageError('That picture is too large to open. Try one under 25 MB.');
  }

  const bitmap = await decode(file);

  try {
    /* The square, taken from the middle. A phone portrait is 3:4, so the crop
       is the full width and the middle of the height — which is where a face
       is, in every photograph anybody has ever taken of a person. */
    const edge = Math.min(bitmap.width, bitmap.height);
    const sx = Math.round((bitmap.width - edge) / 2);
    const sy = Math.round((bitmap.height - edge) / 2);

    // Never upscale. A 200px original stays 200px rather than being stretched
    // to 512 and stored four times larger for no extra detail.
    const size = Math.min(AVATAR_SIZE, edge);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (!context) throw new AvatarImageError('This browser could not process the picture.');

    /* The default is 'low' on several browsers, which produces visible aliasing
       when a twelve-megapixel photograph is reduced by a factor of six. */
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, sx, sy, edge, edge, 0, 0, size, size);

    const blob = await encode(canvas);

    return new File([blob], 'avatar.webp', { type: 'image/webp' });
  } finally {
    // Bitmaps hold their pixels outside the JS heap and are not collected with
    // the rest. A few of these left behind is tens of megabytes on a phone.
    bitmap.close?.();
  }
}

async function decode(file: File): Promise<ImageBitmap> {
  try {
    /* createImageBitmap reads EXIF orientation on every browser that matters,
       which <img> does not do consistently — without it a picture taken in
       portrait is stored on its side, and the person is looking sideways out
       of every list they appear in. */
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new AvatarImageError(
      'That picture could not be opened. A JPEG or PNG works best.',
    );
  }
}

function encode(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new AvatarImageError('The picture could not be saved.'));
      },
      'image/webp',
      QUALITY,
    );
  });
}

/**
 * Initials, for everybody who has not set a picture — which is most people, for
 * ever. This is the ordinary case, not the fallback.
 *
 * Two letters at most: three initials in a 32px circle is a smudge. Takes the
 * first and last words, so "Mohammed Ali Al Mansoori" reads MM rather than MA,
 * which is what somebody would write on a folder.
 */
export function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';

  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase();
}
